import { twMerge } from '@shared/themes';
import Image from 'next/image';
import Link from 'next/link';
import type { ComponentProps } from 'react';
import { ContentGraphics } from '../../../../ContentGraphics/ContentGraphics';

type ProductLaunchpadItemProps = {
  href: string;
  title: string;
  description: string;
  image: string;
  alt: string;
  featured?: boolean;
  imageClassName?: string;
} & ComponentProps<'div'>;

/**
 * ProductLaunchpadItem component is a component that displays a product launchpad item on home page.
 * It displays a title, description and an image.
 * When featured, the image becomes the card's full background on mobile, with the title and description overlaid on top of it.
 *
 * @param {ProductLaunchpadItemProps} props - The props for the ProductLaunchpadItem component.
 * @param {string} props.href - The href of the product launchpad item.
 * @param {string} props.title - The title of the product launchpad item.
 * @param {string} props.description - The description of the product launchpad item.
 * @param {string} props.image - The image of the product launchpad item.
 * @param {string} props.alt - The alt of the product launchpad item.
 * @param {boolean} [props.featured] - Whether the item should use the mobile-only image-as-background overlay layout.
 * @param {string} [props.imageClassName] - Extra classes for the image (object-position, scale, etc.).
 * @returns {JSX.Element} The ProductLaunchpadItem component.
 */
function ProductLaunchpadItem({ href, title, description, image, alt, featured = false, imageClassName, ...props }: ProductLaunchpadItemProps) {
  return (
    <ContentGraphics {...props}>
      <Link href={href} className="dark relative h-full min-h-49 md:min-h-74 flex flex-col justify-between bg-secondary">
        <div className={twMerge('pt-4 px-1.5 xs:px-4', featured && 'max-md:relative max-md:z-20')}>
          <h5 className="md:heading-4">{title}</h5>
          <p className="paragraph-s md:paragraph mt-1">{description}</p>
        </div>
        {featured && <div aria-hidden className="pointer-events-none absolute inset-x-0 top-0 z-10 h-20 bg-gradient-to-b from-secondary to-transparent md:hidden" />}
        <div className={twMerge('relative w-full flex-1 overflow-hidden', featured && 'max-md:absolute max-md:inset-0')}>
          <Image
            src={image}
            fill
            sizes="(min-width: 768px) 320px, 335px"
            className={twMerge(
              'object-cover',
              featured && 'max-md:left-auto! max-md:top-auto! max-md:right-[-15px]! max-md:bottom-0! max-md:h-[83%]! max-md:w-full! max-md:max-w-[250px]',
              imageClassName
            )}
            alt={alt}
          />
        </div>
      </Link>
    </ContentGraphics>
  );
}

export { ProductLaunchpadItem };
